import { ref, computed, type Ref } from 'vue';
import { useProducts as productsModel } from '@aviata/booking-composable';
import type { Airflow } from '@aviata/platform-types';
import type {
  MarkedProduct,
  ProductsState,
  ProductState,
} from '@aviata/products-fns';
import type { PassengersData } from '@aviata/booking-data';

import { roamingConfig } from '@/widgets/roaming';

import { productsLib, type productsTypes } from '@/entities/products';

import type { UseProductsStateReturn } from '../types';
import { mapMedicalInsurance } from '../lib';
import { getProducts } from '../api';

type UseServicesProps = {
  offerId: string;
  initialState: ProductsState | null;
  packageInfo: productsTypes.ExtendedPackageInfo | null;
  passengers: PassengersData;
  roamingProducts: Airflow.Products.Service[] | null;
  /**
   * Gate for the underlying /airflow/products fetch. QuickBooking passes a
   * ref that flips to true only after the initial fare-change settles, so the
   * HTTP call doesn't race the changeFare write.
   */
  fetchEnabled?: Ref<boolean>;
};

export function useProductsState({
  offerId,
  initialState,
  packageInfo,
  passengers,
  roamingProducts,
  fetchEnabled,
}: UseServicesProps): UseProductsStateReturn {
  const products = productsModel({
    offerId,
    fetchProducts: () => getProducts(offerId, fetchEnabled),
  });

  const isRoamingSelected = ref(roamingProducts?.length > 0);
  const selectedProducts = ref<MarkedProduct[]>([]);

  const currentPackage = computed(() =>
    products.packages.value.find(
      productPackage => productPackage.id === products.packageInfo.value.id,
    ),
  );

  const visibleProducts = computed(() =>
    productsLib.getVisibleProducts({
      state: products.state.value,
      products: products.items.value,
      passengers,
    }),
  );

  const roamingProduct = computed(() =>
    products.items.value.find(
      product => product.service === roamingConfig.ROAMING,
    ),
  );

  const flightDelayProducts = computed(() =>
    products.items.value.filter(
      product => product.service === 'flight-delay-insurance',
    ),
  );

  const visiblePackages = computed(() =>
    productsLib.getVisiblePackages(products.packages.value, passengers),
  );

  const medicalInsuranceProducts = computed(() =>
    productsLib
      .getMedicalInsuranceProduct(products.items.value)
      .map(mapMedicalInsurance),
  );

  function init(options: { skipDefaultSelection?: boolean } = {}) {
    return products.init().then(() => {
      if (initialState && packageInfo) {
        applyInitialState(initialState, packageInfo);
      } else if (!options.skipDefaultSelection) {
        selectDefaultPackage();
      }
    });
  }

  function selectDefaultPackage() {
    const defaultPackage = visiblePackages.value.find(
      productPackage => productPackage.$meta.select_by_default,
    );
    if (defaultPackage) {
      onPackageSelect(defaultPackage);
    }
  }

  function onPackageSelect(selectedPackage: Airflow.Products.Package) {
    if (currentPackage.value) {
      currentPackage.value.products.forEach(product =>
        removeProduct(product.service),
      );
    }
    selectedPackage.products.forEach(product =>
      addProductAsPartOfPackage(product as MarkedProduct),
    );
    const packageInfo = productsLib.getPackageInfo(selectedPackage);
    products.onUpdate({ productsState: products.state.value, packageInfo });
  }

  function onPackageUnselect() {
    if (currentPackage.value) {
      currentPackage.value.products.forEach(product =>
        removeProduct(product.service),
      );
    }
    const payload = {
      productsState: products.state.value,
      packageInfo: {
        title: '',
        price: 0,
        id: '',
        type: '',
      },
    };
    products.onUpdate(payload);
  }

  function addProductAsPartOfPackage(product: MarkedProduct) {
    addProduct(product, true);
  }

  function addProductSeparately(productName: Airflow.Products.Service) {
    const selectedProduct = products.items.value.find(
      product => product.service === productName,
    );
    if (!selectedProduct) {
      return;
    }
    selectedProducts.value.push(selectedProduct);
    addProduct(selectedProduct, false);
  }

  function setProductData(service: Airflow.Products.Service, value: number) {
    const selectedProduct = products.items.value.find(
      product => product.service === service,
    );
    if (!selectedProduct) {
      return;
    }

    products.onUpdate({
      productsState: {
        ...products.state.value,
        [selectedProduct.service]: {
          ...products.state.value[selectedProduct.service],
          price: value,
          data: {
            amount: value,
          },
        },
      },
      packageInfo: products.packageInfo.value,
    });
  }

  function toggleRoamingProduct() {
    isRoamingSelected.value = !isRoamingSelected.value;
  }

  function addProduct(product: MarkedProduct, selectedAsPackage: boolean) {
    const updatedState = productsLib.getSelectedState({
      product,
      productState: products.state.value[product.service],
      selectedAsPackage,
    });

    const payload = {
      productsState: {
        ...products.state.value,
        [product.service]: updatedState,
      },
      packageInfo: products.packageInfo.value,
    };

    products.onUpdate(payload);
  }

  function removeProduct(productName: Airflow.Products.Service) {
    const updatedState = productsLib.getUnselectedState(
      products.state.value[productName],
    );

    selectedProducts.value = selectedProducts.value.filter(
      product => product.service !== productName,
    );

    const payload = {
      productsState: {
        ...products.state.value,
        [productName]: updatedState,
      },
      packageInfo: products.packageInfo.value,
    };

    products.onUpdate(payload);
  }

  /**
   * Re-fetches /airflow/products and resets the local items/state map without
   * re-applying the captured `initialState`. Used after fare-change to mirror
   * the classic flow's products refetch — the captured snapshot in this
   * composable's closure is from QuickBookingPage mount, so the wrapper's
   * `init` would re-apply it; this bypass goes straight to the booking-
   * composable's products model so we get fresh server IDs and a clean state
   * map (`createInitialState`).
   */
  function refetch() {
    return products.init();
  }

  function resetSelections() {
    selectedProducts.value = [];

    const resetState: ProductsState = {};
    Object.entries(products.state.value).forEach(([service, productState]) => {
      if (!productState) {
        return;
      }
      resetState[service as Airflow.Products.Service] = {
        ...productState,
        id: null,
        price: 0,
        initialPrice: 0,
        selectedAsPackage: false,
        data: undefined,
      };
    });

    products.onUpdate({
      productsState: resetState,
      packageInfo: {
        title: '',
        price: 0,
        id: '',
        type: '',
      },
    });
  }

  function applyInitialState(
    cachedState: ProductsState,
    packageInfo: productsTypes.ExtendedPackageInfo,
  ) {
    const productsServices = Object.entries(cachedState) as [
      Airflow.Products.Service,
      ProductState,
    ][];

    const initialPackage = visiblePackages.value.find(
      productsPackage => packageInfo.id === productsPackage.id,
    );

    if (initialPackage) {
      onPackageSelect(initialPackage);
    }

    const visibleServices = new Set<string>(
      visibleProducts.value.map(product => product.service),
    );

    const nonPackageProducts = productsServices.filter(
      ([serivceName]) =>
        cachedState[serivceName]?.id &&
        !cachedState[serivceName]?.selectedAsPackage &&
        visibleServices.has(serivceName),
    );

    nonPackageProducts.forEach(([serviceName, state]) => {
      addProductSeparately(serviceName);

      if (state.data?.amount) {
        setProductData(serviceName, state.data.amount);
      }
    });
  }

  return {
    init,
    onPackageSelect,
    onPackageUnselect,
    removeProduct,
    setProductData,
    addProductSeparately,
    medicalInsuranceProducts,
    state: products.state,
    packageInfo: products.packageInfo,
    updateProductState: products.onUpdate,
    visibleProducts,
    products: products.items,
    packages: visiblePackages,
    isLoading: products.isLoading,
    roamingProduct,
    isRoamingSelected,
    toggleRoamingProduct,
    flightDelayProducts,
    selectedProducts,
    resetSelections,
    refetch,
  };
}
